import { useQuery } from '@apollo/client/react';
import { useUser } from '@context/user-context';
import { CelloReferralCodeDocument } from '@graphql/queries/cello/referral-code.graphql';
import { getCachedCelloReferralCode } from '@modules/cello-referral';
import { useEffect, useState } from 'react';

export interface CelloReferral {
  /** The cached Cello referral code, or `null` when the user wasn't referred. */
  referralCode: string | null;
  isValid: boolean;
  /**
   * The backend positively rejected the code, as opposed to the request having
   * failed. Only a rejection is safe to act on destructively: treating a network
   * error as a dead code would throw away a discount the user is still owed.
   */
  isRejected: boolean;
  referrerFirstName: string | null;
  referrerImageUrl: string | null;
  loading: boolean;
}

/**
 * Resolves the cached Cello referral code against the backend.
 *
 * The `celloReferralCode` query is login-gated, so this only resolves once a user
 * is authenticated — callers reached before signup completes will see
 * `isValid: false` with `loading: true`.
 */
export function useCelloReferral(): CelloReferral {
  const { user, loading: userLoading } = useUser();

  // localStorage isn't readable during SSR, so the code is picked up after mount
  // rather than during render.
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [hasReadCache, setHasReadCache] = useState(false);

  useEffect(() => {
    setReferralCode(getCachedCelloReferralCode());
    setHasReadCache(true);
  }, []);

  const {
    data,
    loading: queryLoading,
    error,
  } = useQuery(CelloReferralCodeDocument, {
    variables: { code: referralCode ?? '' },
    skip: !referralCode || !user?.userId,
  });

  return {
    referralCode,
    isValid: data?.celloReferralCode.isValid ?? false,
    isRejected: Boolean(data) && !error && data?.celloReferralCode.isValid === false,
    referrerFirstName: data?.celloReferralCode.referrerFirstName ?? null,
    referrerImageUrl: data?.celloReferralCode.referrerImageUrl ?? null,
    loading: !hasReadCache || (Boolean(referralCode) && (userLoading || queryLoading)),
  };
}
